import * as LabelPrimitive from '@radix-ui/react-label'
import { InfoIcon } from 'lucide-react'
import { useRef } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useIsOverflowing } from '~/hooks/useIsOverflowing'
import { InputWrapperProps } from './types'

export function InputWrapper({
  children,
  wrapperClassName,
  errorText,
  helperText,
  label,
  required,
  tooltip,
  showDirtyIndicator,
}: InputWrapperProps) {
  const subscriptTextRef = useRef<HTMLParagraphElement>(null)
  const labelRef = useRef<HTMLLabelElement>(null)
  const subscriptText = errorText || helperText
  const isSubscriptTextOverflowing = useIsOverflowing(subscriptTextRef)
  const isLabelOverflowing = useIsOverflowing(labelRef)

  return (
    <div className={cn('input-wrapper w-full flex flex-col gap-1 p-1', wrapperClassName)}>
      {label && (
        <div className="label-section relative flex items-center text-xs">
          {showDirtyIndicator && (
            <div className="absolute -left-3 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-blue-9" />
          )}
          <Tooltip open={!isLabelOverflowing ? false : undefined}>
            <TooltipTrigger asChild>
              <LabelPrimitive.Root ref={labelRef} className="block min-w-0 truncate">
                {label}
              </LabelPrimitive.Root>
            </TooltipTrigger>
            <TooltipContent className="whitespace-normal break-all">{label}</TooltipContent>
          </Tooltip>
          {required && <div className="ms-0.5 shrink-0 text-red-9">*</div>}
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="ms-1 h-3 w-3 shrink-0 text-gray-9" />
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">{tooltip}</TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
      {children}
      {/* Only enable tooltip if overflow */}
      {subscriptText && (
        <Tooltip open={!isSubscriptTextOverflowing ? false : undefined}>
          <TooltipTrigger asChild>
            <span className="input-footer text-start">
              {/* Margin left 1 pixel to line up under border */}
              <p
                ref={subscriptTextRef}
                className={cn(
                  'subscript-text text-xs ml-[1px] leading-4 truncate',
                  errorText ? 'text-red-9' : 'text-gray-9',
                )}
              >
                {subscriptText}
              </p>
            </span>
          </TooltipTrigger>
          <TooltipContent>{subscriptText}</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
